import { CheckCircle2, Download, Loader2, TriangleAlert, Upload } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { api } from "@/api";
import { formatError } from "@/auth";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

const CHUNK = 5 * 1024 * 1024;

function JobRow({ job }) {
  const icon = job.status === "done" ? <CheckCircle2 size={16} className="pj-ok" /> : job.status === "error" ? <TriangleAlert size={16} className="pj-late" /> : <Loader2 size={16} className="pj-spin" />;
  const c = job.counts;
  return (
    <li data-testid={`bc-job-${job.id}`}>
      {icon}
      <div>
        <strong>{job.project_name || job.label}</strong>
        <small>{job.status === "error" ? job.error : job.status === "done" ? `${c.lists} listes · ${c.todos} tâches · ${c.messages} discussions · ${c.docs} documents · ${c.files} fichiers · ${c.comments} commentaires` : job.step}</small>
      </div>
      {job.status === "done" && <Link to={`/projets/${job.project_id}`} className="link-button" data-testid={`bc-job-open-${job.id}`}>Ouvrir</Link>}
    </li>
  );
}

export function BasecampImport({ onClose, onImported }) {
  const [jobs, setJobs] = useState([]);
  const [url, setUrl] = useState("");
  const [upload, setUpload] = useState(null);
  const input = useRef(null);
  const load = useCallback(() => api.get("/basecamp-import").then(({ data }) => setJobs(data)).catch(() => {}), []);
  const running = jobs.some((j) => j.status === "running");
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (!running) { onImported(); return undefined; } const t = setInterval(load, 3000); return () => clearInterval(t); }, [running, load, onImported]);
  const fromUrl = async (e) => {
    e.preventDefault();
    setUpload({ name: "Téléchargement du ZIP…", pct: null });
    try { await api.post("/basecamp-import/url", { url: url.trim() }); setUrl(""); } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    setUpload(null); load();
  };
  const fromFile = async (file) => {
    const id = crypto.randomUUID();
    const total = Math.max(1, Math.ceil(file.size / CHUNK));
    try {
      for (let i = 0; i < total; i += 1) {
        setUpload({ name: file.name, pct: Math.round((i / total) * 100) });
        const fd = new FormData();
        fd.append("upload_id", id); fd.append("index", i); fd.append("total", total); fd.append("filename", file.name);
        fd.append("chunk", file.slice(i * CHUNK, (i + 1) * CHUNK), "chunk");
        await api.post("/basecamp-import/chunk", fd, { timeout: 300000 });
      }
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    setUpload(null); load();
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent aria-describedby={undefined} className="pj-dialog" data-testid="bc-import-dialog">
        <DialogTitle className="pj-dialog-title">Importer depuis Basecamp</DialogTitle>
        <p className="pj-sub">Un ZIP d'export Basecamp 2 par projet : to-do lists, tâches, discussions, commentaires, documents et fichiers. Les personnes sans compte deviennent des invités (aucun e-mail envoyé). Réimporter le même projet le remplace.</p>
        <div className="pj-drop" onClick={() => !upload && input.current?.click()} data-testid="bc-dropzone">
          <Upload size={20} /><span>{upload ? `${upload.name}${upload.pct !== null ? ` ${upload.pct}%` : ""}` : "Choisir un fichier ZIP"}</span>
          <input ref={input} type="file" accept=".zip" hidden onChange={(e) => { const f = e.target.files[0]; e.target.value = ""; if (f) fromFile(f); }} data-testid="bc-file-input" />
        </div>
        <form className="pj-inline-form pj-full" onSubmit={fromUrl}>
          <input placeholder="…ou collez le lien du ZIP (https://…)" value={url} onChange={(e) => setUrl(e.target.value)} disabled={!!upload} data-testid="bc-url-input" />
          <button type="submit" className="secondary-button" disabled={!!upload || !url.trim()} data-testid="bc-url-submit"><Download size={15} />Importer</button>
        </form>
        {jobs.length > 0 && <ul className="pj-jobs" data-testid="bc-jobs">{jobs.map((j) => <JobRow key={j.id} job={j} />)}</ul>}
      </DialogContent>
    </Dialog>
  );
}
